import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';
import { ReadNotificationRequest, ReadNotificationResponse } from '../types/apiGeneratedTypes';

export interface StaffMember {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  email: string;
}

export interface StaffSearchResponse {
  users: StaffMember[];
}

export interface StaffSearchParams {
  q: string;
  limit: number;
  offset: number;
}

export interface Alert {
  id: number;
  title: string;
  type: string;
  is_read: boolean;
  member_name?: string;
  appointment_date?: string;
  note_id?: number;
  appointment_id?: number;
  metadata?: {
    member_id?: string;
    member_user_id?: string;
    member_name?: string;
    note_id?: number;
    appointment_id?: number;
    status?: string;
    overdue_since?: string;
    extra_info?: string;
  };
  created_at?: string;
  updated_at?: string;
}

export enum AlertType {
  NOTE_MENTION = "note_mention",
  BIO_AGE_CALCULATED = "bio_age_calculated",
  BIOMARKER_PENDING = "biomarker_pending",
  BIOMARKER_AWAITING = "biomarker_awaiting",
  BIOMARKER_URGENT = "biomarker_urgent",
  REPORT_REVIEW_OVERDUE = "report_review_overdue",
  REPORT_DISCUSSION_OVERDUE = "report_discussion_overdue",
  MEDICAL_CARD_INCOMPLETE = "medical_card_incomplete",
  TULI_APPOINTMENT_COMPLETE_OVERDUE = "tuli_appointment_complete_overdue",
  NUTRITIONIST_INVITE_OVERDUE = "nutritionist_invite_overdue",
  FUTURE_TEST = "future_test_today",
}

export interface StaffAlertsResponse {
  alerts: Alert[];
}

export const searchStaff = async (
  query: string,
  limit: number = 10,
  offset: number = 0
): Promise<StaffSearchResponse> => {
  try {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}staff/search?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to search staff');
  }
};

export const getStaffAlerts = async (): Promise<StaffAlertsResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}staff/alerts`
    );

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch staff alerts');
  }
};

export interface TaggedUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  display_name: string;
  username: string;
}

export const getStaffForTagging = async (
  search?: string,
  limit: number = 50
): Promise<TaggedUser[]> => {
  try {
    const response = await searchStaff(search || '', limit, 0);
    
    const staff = response.users.map((member: StaffMember) => ({
      id: member.id,
      first_name: member.first_name,
      last_name: member.last_name,
      role: member.role,
      display_name: `${member.first_name} ${member.last_name}`,
      username: `${member.first_name}${member.last_name}`.toLowerCase()
    }));

    return staff;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to fetch staff for tagging');
  }
};

export const readStaffNotification = async (
  notificationId: number,
  isRead: boolean = true
): Promise<ReadNotificationResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}notification/staff/read`,
      { notification_id: notificationId, is_read: isRead } as ReadNotificationRequest
    );

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to read staff notification');
  }
};
